import {useEffect, useState, type FormEvent} from 'react';
import {EMAIL_PASSWORD_POLICY} from '../src/auth-policy';
import {ErrorNotice, Loading, shortId} from './components';
import {APP_BASE, Link, navigate, useSearch} from './router';
import type {AuthMode, HermesState} from './lib/types';
import type {hermesActions} from './lib/store';
import './auth.css';

type Props = {state: HermesState; actions: typeof hermesActions};
export type {AuthMode} from './lib/types';

function returnToWorkspace(search: string, returnTo?: string): string {
  const requested = returnTo ?? new URLSearchParams(search).get('next');
  if (requested === null || requested === undefined) return `${APP_BASE}/workspace`;
  const target = new URL(requested, window.location.origin);
  if (target.origin !== window.location.origin || !target.pathname.startsWith(`${APP_BASE}/`) ||
      ['login', 'register', 'setup-login'].some(page => target.pathname === `${APP_BASE}/${page}`)) {
    throw new Error('The sign-in return address must be a page in EightBots.');
  }
  return `${target.pathname}${target.search}`;
}

export function AccountIdentity({state, actions}: Props) {
  const authenticated = state.session.data;
  if (!authenticated) return null;
  return <div className="account-identity"><span>{authenticated.authMethod === 'google' ? 'Signed in with Google' : 'Signed in'}</span>
    <strong>{authenticated.email ?? (authenticated.address === null ? authenticated.accountId : shortId(authenticated.address))}</strong>
    <button type="button" className="text-button" disabled={state.operation.label !== null}
      onClick={() => void actions.signOut().catch(() => undefined)}>Sign out</button>
  </div>;
}

export function AuthPage({state, actions, mode, returnTo}: Props & {mode: AuthMode; returnTo?: string}) {
  const search = useSearch();
  const destination = returnToWorkspace(search, returnTo);
  const [setupToken] = useState(() => mode === 'setup-login'
    ? new URLSearchParams(window.location.hash.slice(1)).get('token') : null);
  const [showPassword, setShowPassword] = useState(false);
  const busy = state.operation.label !== null;
  const newPassword = mode !== 'login';
  const label = mode === 'login' ? 'Sign in' : mode === 'register' ? 'Create account' : 'Save sign-in details';
  const next = `?${new URLSearchParams({next: destination})}`;

  useEffect(() => {
    if (mode === 'setup-login' && window.location.hash) {
      window.history.replaceState(window.history.state, '', `${window.location.pathname}${window.location.search}`);
    }
  }, [mode]);
  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    const email = String(data.get('email') ?? '');
    const password = String(data.get('password') ?? '');
    try {
      if (mode === 'login') await actions.signIn(email, password);
      else if (mode === 'register') await actions.register(email, password);
      else {
        if (setupToken === null) throw new Error('Open your account setup link before you set a password.');
        await actions.setupLogin(setupToken, email, password);
      }
      form.reset();
      if (`${window.location.pathname}${window.location.search}` !== destination) navigate(destination);
    } catch { /* The shared operation error is shown below. */ }
  }

  return <section className="auth-page">
    <div className="auth-content"><header><h1>{mode === 'login' ? 'Welcome back.' : mode === 'register' ? 'Meet your team.' : 'Set up your sign-in.'}</h1>
      <p>{mode === 'login' ? 'Sign in to continue your work.' : mode === 'register'
        ? 'Create an account to give work to your team.'
        : 'Choose an email and password for your existing account. Your saved work and connected model key stay in this account.'}</p></header>
      {state.session.status === 'loading' || state.session.status === 'idle' ? <Loading/>
        : state.session.status === 'error' ? <><ErrorNotice error={state.session.error}/><button type="button" className="text-button" onClick={() => void actions.refresh().catch(() => undefined)}>Retry account connection</button></>
          : mode === 'setup-login' && setupToken === null ? <div className="auth-link-required"><p>Open your account setup link to continue.</p><Link className="button" href={`${APP_BASE}/login${next}`}>Go to sign-in</Link></div>
            : <form className="auth-form" onSubmit={event => void submit(event)}>
              <label className="field"><span>Email</span><input type="email" name="email" autoComplete="username" autoCapitalize="none"
                autoCorrect="off" spellCheck={false} maxLength={EMAIL_PASSWORD_POLICY.emailMaxLength} required disabled={busy}/></label>
              <label className="field"><span>Password</span><input type={showPassword ? 'text' : 'password'} name="password"
                autoComplete={newPassword ? 'new-password' : 'current-password'} required disabled={busy}
                minLength={newPassword ? EMAIL_PASSWORD_POLICY.minLength : undefined} maxLength={EMAIL_PASSWORD_POLICY.maxLength}
                aria-describedby={newPassword ? 'password-help' : undefined}/></label>
              <div className="auth-password-options">{newPassword && <p id="password-help">Use at least {EMAIL_PASSWORD_POLICY.minLength} characters.</p>}
                <button type="button" className="text-button" aria-pressed={showPassword} onClick={() => setShowPassword(!showPassword)}>{showPassword ? 'Hide password' : 'Show password'}</button></div>
              <ErrorNotice error={state.operation.scope === mode ? state.operation.error : null}/>
              <button className="button auth-submit" type="submit" disabled={busy}>{busy ? state.operation.label : label}</button>
              {mode !== 'setup-login' && <div className="auth-google"><span>or</span>
                <button type="button" className="button" disabled={busy || state.config.status !== 'ready' || !state.config.data?.googleConfigured}
                  onClick={() => void actions.signInGoogle(destination, mode).catch(() => undefined)}>Continue with Google</button>
                {state.config.status === 'ready' && !state.config.data?.googleConfigured && <p>Google sign-in setup is in progress.</p>}
              </div>}
              {mode !== 'setup-login' && <p className="auth-switch">{mode === 'login' ? 'New to EightBots?' : 'Already have an account?'}{' '}
                <Link href={`${APP_BASE}/${mode === 'login' ? 'register' : 'login'}${next}`}>{mode === 'login' ? 'Create an account' : 'Sign in'}</Link></p>}
            </form>}
    </div>
  </section>;
}
